'use client';

import React from 'react';
import styled from 'styled-components';
import { Icon } from '@liftango/ui-icons';
import { useDensity } from '@liftango/ui-tokens';

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  shortcut?: string;
  'aria-label'?: string;
  className?: string;
}

const Wrapper = styled.div<{ $height: number }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  height: ${(p) => p.$height}px;
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  padding: 0 ${(p) => p.theme.spaceAlias.md};
  background: ${(p) => p.theme.colors.neutral.bg};
  color: ${(p) => p.theme.colors.neutral.inkMute};

  &:focus-within {
    border-color: ${(p) => p.theme.colors.brand.primary};
    outline: ${(p) => p.theme.focus.width} solid ${(p) => p.theme.colors.brand.primary};
    outline-offset: ${(p) => p.theme.focus.insetOffset};
  }
`;

const Input = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-size: ${(p) => p.theme.fontSize.body};
  font-family: ${(p) => p.theme.fontFamily.ui};
  color: ${(p) => p.theme.colors.neutral.ink};
  flex: 1;
  padding: 0;
  min-width: 0;
  &::placeholder {
    color: ${(p) => p.theme.colors.neutral.inkFaint};
  }
`;

const Kbd = styled.kbd`
  font-size: ${(p) => p.theme.fontSize.xs};
  font-family: ${(p) => p.theme.fontFamily.mono};
  padding: 1px 5px;
  border-radius: ${(p) => p.theme.radius.sm};
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  color: ${(p) => p.theme.colors.neutral.inkMute};
  flex-shrink: 0;
`;

export function SearchInput({
  value,
  onChange,
  placeholder,
  shortcut,
  'aria-label': ariaLabel,
  className,
}: SearchInputProps) {
  const density = useDensity();
  return (
    <Wrapper className={className} $height={density.controlHeight}>
      <Icon name="magnifying-glass" />
      <Input
        role="searchbox"
        aria-label={ariaLabel ?? placeholder ?? 'Search'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {shortcut && <Kbd>{shortcut}</Kbd>}
    </Wrapper>
  );
}
